import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import {
  createRole,
  deleteRole,
  listPermissions,
  listRoles,
  RoleSummary,
  setRolePermissions,
} from '../../api/roles';
import { PageHeader } from '../../components/PageHeader';
import { RolePermissionsDialog } from './RolePermissionsDialog';

export const RolesPage = () => {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });
  const permissionsQuery = useQuery({ queryKey: ['permissions'], queryFn: listPermissions });

  const [permissionsDialogRole, setPermissionsDialogRole] = useState<RoleSummary | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['roles'] });

  const createMutation = useMutation({
    mutationFn: () => createRole({ name: newRoleName }),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      setNewRoleName('');
      setError(null);
    },
    onError: (err) =>
      setError(isAxiosError(err) ? err.response?.data?.message ?? 'Failed to create role' : 'Failed to create role'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteRole(id),
    onSuccess: invalidate,
  });

  const savePermissionsMutation = useMutation({
    mutationFn: ({ roleId, permissionIds }: { roleId: number; permissionIds: number[] }) =>
      setRolePermissions(roleId, permissionIds),
    onSuccess: invalidate,
  });

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={SecurityOutlinedIcon}
        color="#3E1E73"
        title="Roles & permissions"
        subtitle="Manage roles and fine-grained permissions."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New role
          </Button>
        }
      />

      <Stack spacing={2}>
        {rolesQuery.data?.map((role) => (
          <Card key={role.id} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">{role.name}</Typography>
                    {role.isSystem && <Chip size="small" label="System" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {role.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role.userCount} user(s) · {role.permissions.length} permission(s)
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Manage permissions">
                    <IconButton onClick={() => setPermissionsDialogRole(role)}>
                      <TuneIcon />
                    </IconButton>
                  </Tooltip>
                  {!role.isSystem && (
                    <Tooltip title={role.userCount > 0 ? 'Cannot delete: users assigned' : 'Delete role'}>
                      <span>
                        <IconButton
                          disabled={role.userCount > 0}
                          onClick={() => deleteMutation.mutate(role.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <RolePermissionsDialog
        open={!!permissionsDialogRole}
        role={permissionsDialogRole}
        permissionsByModule={permissionsQuery.data ?? {}}
        onClose={() => setPermissionsDialogRole(null)}
        onSave={async (roleId, permissionIds) => {
          await savePermissionsMutation.mutateAsync({ roleId, permissionIds });
        }}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Role name"
              autoFocus
              fullWidth
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newRoleName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
